import { StandardEvents } from '@shopify/events';

/**
 * Refreshes the cart drawer's "You may also like" row.
 *
 * Earlier versions of this file relied on a custom element's
 * connectedCallback/attributeChangedCallback to detect cart updates. That
 * worked most of the time, but the drawer's own update mechanism doesn't
 * always recreate or re-attribute that element on every cart change (it can
 * intermittently patch the DOM in a way that skips firing those callbacks),
 * so the block could silently stop refreshing after a couple of quick
 * updates until a full page reload.
 *
 * This version instead listens for the theme's own StandardEvents.cartLinesUpdate
 * event (the same one cart-drawer.js uses for its sticky-summary logic) and,
 * once that event's promise resolves (meaning the DOM has already settled),
 * re-queries the current element fresh and re-fetches. That sidesteps any
 * assumption about a specific DOM node surviving cart updates.
 */

const SELECTOR = '#cart-drawer-recommendations';

let requestToken = 0;

function formatMoney(cents, currency) {
  try {
    return new Intl.NumberFormat(navigator.language || 'en-US', {
      style: 'currency',
      currency
    }).format(cents / 100);
  } catch (e) {
    return '$' + (cents / 100).toFixed(2);
  }
}

function buildMarkup(products, currency) {
  const cards = products
    .map((p) => {
      const image = p.featured_image || (p.images && p.images[0]) || '';
      return `
        <a href="${p.url}" class="cart-drawer-recs__card">
          <span class="cart-drawer-recs__image-wrap">
            ${
              image
                ? `<img src="${image}" alt="" width="100" height="100" loading="lazy" class="cart-drawer-recs__image">`
                : ''
            }
          </span>
          <span class="cart-drawer-recs__title">${p.title}</span>
          <span class="cart-drawer-recs__price">${formatMoney(p.price, currency)}</span>
        </a>
      `;
    })
    .join('');

  return `
    <p class="cart-drawer-recs__heading">You may also like</p>
    <div class="cart-drawer-recs__row">${cards}</div>
  `;
}

async function refreshRecommendations() {
  const el = document.querySelector(SELECTOR);
  if (!el) return;

  const productId = el.dataset.productId;
  if (!productId) {
    el.innerHTML = '';
    return;
  }

  const token = ++requestToken;
  const currency = el.dataset.currency || 'USD';
  const excludeIds = (el.dataset.excludeIds || '').split(',').filter(Boolean);

  const apply = (products) => {
    // A newer request has since been kicked off - ignore this one.
    if (token !== requestToken) return;

    // Re-query fresh rather than reusing `el`, in case the drawer replaced
    // the node while this fetch was in flight.
    const currentEl = document.querySelector(SELECTOR);
    if (!currentEl || currentEl.dataset.productId !== productId) return;

    const filtered = products.filter((p) => !excludeIds.includes(String(p.id))).slice(0, 4);
    currentEl.innerHTML = filtered.length ? buildMarkup(filtered, currency) : '';
  };

  const baseUrl = `/recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=8`;

  try {
    const res = await fetch(`${baseUrl}&intent=complementary`);
    const data = await res.json();
    let products = data.products || [];

    if (!products.length) {
      // Fall back to related-intent recommendations if no complementary
      // matches exist for this product yet.
      const res2 = await fetch(`${baseUrl}&intent=related`);
      const data2 = await res2.json();
      products = data2.products || [];
    }

    apply(products);
  } catch (e) {
    if (token === requestToken) {
      const currentEl = document.querySelector(SELECTOR);
      if (currentEl) currentEl.innerHTML = '';
    }
  }
}

// Initial page load (including cases where the drawer already has items
// from a previous session, before any cart update event fires).
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', refreshRecommendations, { once: true });
} else {
  refreshRecommendations();
}

// Every subsequent cart change (add, remove, quantity update), from
// anywhere on the page.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise?.then(() => refreshRecommendations()).catch(() => {});
});
